import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mic, ClipboardPaste, Bot, Save, Check, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { extractVideoId, streamSummary } from "./utils";
import { autoSaveSummary } from "./autoSave";
import ReactMarkdown from "react-markdown";

const MODEL_OPTIONS = [
  { value: "auto", label: "🤖 Auto" },
  { value: "gemini-2.5-pro", label: "⚡ Gemini 2.5 Pro" },
  { value: "gpt-5", label: "🎯 GPT-5" },
  { value: "gpt-5.2", label: "🧠 GPT-5.2" },
  { value: "gemini-3.1-pro", label: "🏆 Gemini 3.1 Pro" },
];

interface Props {
  userId?: string;
}

export function DeepDiveMode({ userId }: Props) {
  const [url, setUrl] = useState("");
  const [model, setModel] = useState("auto");
  const [conversation, setConversation] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [meta, setMeta] = useState<{ author?: string; model?: string; language?: string }>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  const isValidYoutubeUrl = (u: string) =>
    /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/.test(u);

  const handleStart = async () => {
    const trimmed = url.trim();
    if (!trimmed || !isValidYoutubeUrl(trimmed)) {
      toast({ title: "Invalid URL", description: "Please enter a valid YouTube URL.", variant: "destructive" });
      return;
    }
    setConversation("");
    setStatus("loading");
    setIsProcessing(true);
    setSaved(false);
    abortRef.current = new AbortController();

    try {
      const result = await streamSummary(
        trimmed,
        (text) => setConversation(text),
        abortRef.current.signal,
        (m) => setMeta(m),
        model === "auto" ? undefined : model,
        "deep-dive"
      );
      setConversation(result.summary);
      setStatus("done");
      // Auto-save
      const didSave = await autoSaveSummary({
        userId,
        videoUrl: trimmed,
        videoId: extractVideoId(trimmed),
        summaryContent: `[DEEP DIVE PODCAST]\n\n${result.summary}`,
        author: result.author,
      });
      if (didSave) setSaved(true);
    } catch (error: any) {
      if (error.name !== "AbortError") {
        setStatus("error");
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setIsProcessing(false);
    setStatus("idle");
  };

  const handleSave = async () => {
    if (!userId) {
      toast({ title: "Sign in required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("content_summaries").insert({
      user_id: userId,
      video_url: url.trim(),
      video_id: extractVideoId(url.trim()),
      summary_content: `[DEEP DIVE PODCAST]\n\n${conversation}`,
      author: meta.author || null,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setSaved(true);
      toast({ title: "Saved", description: "Deep dive saved to Collected Data." });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(conversation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Count speaker turns for progress estimation
  const turnsCount = (conversation.match(/\*\*🎙️|🎧/g) || []).length;
  const estimatedProgress = Math.min(95, Math.round((turnsCount / 30) * 100));

  return (
    <div className="space-y-6">
      <Card className="border-primary/10">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Deep Dive Podcast</CardTitle>
            <Badge variant="secondary" className="text-[10px]">NotebookLM Style</Badge>
          </div>
          <CardDescription>
            Two AI hosts — Alex & Sam — have an in-depth conversation about the video, exploring insights, challenging ideas, and drawing unexpected connections.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isProcessing}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              disabled={isProcessing}
              title="Paste from clipboard"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text.trim()) { setUrl(text.trim()); toast({ title: "Pasted" }); }
                } catch { toast({ title: "Clipboard access denied", variant: "destructive" }); }
              }}
            >
              <ClipboardPaste className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-muted-foreground" />
              <Select value={model} onValueChange={setModel} disabled={isProcessing}>
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              {isProcessing && (
                <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
              )}
              <Button onClick={handleStart} disabled={isProcessing || !url.trim()}>
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                {isProcessing ? "Generating…" : "Start Deep Dive"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversation Output */}
      {(status === "loading" || status === "done") && (
        <Card className="border-primary/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Podcast Conversation</CardTitle>
                {meta.model && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Bot className="h-3 w-3" /> {meta.model}
                  </Badge>
                )}
                {meta.author && (
                  <Badge variant="outline" className="text-[10px]">
                    📺 {meta.author}
                  </Badge>
                )}
              </div>
              {status === "done" && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving || saved}>
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : saved ? <Check className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    {saving ? "Saving…" : saved ? "Saved" : "Save"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {status === "loading" && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin text-primary" />
                    {turnsCount === 0 ? "Fetching transcript & preparing hosts…" : `${turnsCount} speaker turns generated…`}
                  </span>
                  <span className="font-medium">{estimatedProgress}%</span>
                </div>
                <Progress value={estimatedProgress} className="h-2" />
              </div>
            )}
            <ScrollArea className="max-h-[600px]">
              <div className="prose prose-sm dark:prose-invert max-w-none pr-3 deep-dive-conversation">
                <ReactMarkdown>{conversation}</ReactMarkdown>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
